/**
 * Appointment-time scheduling for the dispatch board.
 *
 * The board is read top-to-bottom as a timeline: whatever the driver has to be
 * at soonest comes first, with a separator naming each day. Everything a task
 * needs for that lives on its route stops, so the times are read back off the
 * generated route rather than off the flat columns — that is the only place the
 * "no appointment required" stops are marked, and a NAR stop must not be
 * mistaken for a stop that is simply still unbooked.
 *
 * Appointment columns are `timestamptz` (migration 138), so these really are
 * instants and may go through `Date` — unlike the naive `timestamp` columns
 * elsewhere in the schema, which must never be parsed this way.
 */

import { ContainerTask } from '../types/container'
import { generateRouteNodes } from './routeGenerator'

/** Sentinel day key for tasks with no appointment on either end. */
export const NO_APPT_KEY = 'no-appt'

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * The one timezone a dispatch day is measured in.
 *
 * Every tenant runs on Pacific time and so does eModal, so "which day is this
 * task" has exactly one right answer and it is not the viewer's clock. Reading
 * a day off the browser gets the same answer only by luck — right for a
 * dispatcher sitting in California, wrong by a day for anyone else, and wrong
 * again the moment a day key is sent to a server running on UTC.
 */
export const DISPATCH_TZ = 'America/Los_Angeles'

/** en-CA yields exactly YYYY-MM-DD, which is the day-key format. */
const DAY_KEY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: DISPATCH_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** 'YYYY-MM-DD' of the Pacific calendar day an instant falls on. */
function dayKeyOf(d: Date): string {
  return DAY_KEY_FMT.format(d)
}

/**
 * A comparable stand-in for "the Pacific day this instant belongs to".
 *
 * Not a real instant — it is local midnight of the Pacific calendar date, which
 * is all the callers need: they only ever compare these to each other or
 * subtract them to count whole days. Building it this way keeps day arithmetic
 * free of the browser's own offset.
 */
function startOfDay(d: Date): Date {
  const [year, month, day] = dayKeyOf(d).split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** '' / 'Pending' / anything unparseable → null. */
function parseAppt(value?: string | null): Date | null {
  if (!value || value === 'Pending') return null
  const d = new Date(value)
  return isNaN(d.getTime()) ? null : d
}

function earlier(a: Date | null, b: Date | null): Date | null {
  if (!a) return b
  if (!b) return a
  return a <= b ? a : b
}

function later(a: Date | null, b: Date | null): Date | null {
  if (!a) return b
  if (!b) return a
  return a >= b ? a : b
}

/**
 * The soonest port and warehouse appointment the task is booked against.
 *
 * Stops that need no appointment, and stops the driver never made, contribute
 * nothing — a NAR warehouse is not "unbooked", it is simply not a time.
 */
export function getApptDates(task: ContainerTask): { port: Date | null; warehouse: Date | null } {
  let port: Date | null = null
  let warehouse: Date | null = null
  let sawWarehouseStop = false

  for (const node of generateRouteNodes(task)) {
    if (node.appointmentRequired === false) {
      if (node.type === 'warehouse') sawWarehouseStop = true
      continue
    }
    if (node.action === 'rejected' || node.status === 'rejected') continue
    if (node.type === 'port') {
      port = earlier(port, parseAppt(node.appointmentTime))
    } else if (node.type === 'warehouse') {
      sawWarehouseStop = true
      warehouse = earlier(warehouse, parseAppt(node.appointmentTime))
    }
  }

  // Rows the generator can't build a route for still carry the flat columns.
  if (!port) port = earlier(port, parseAppt(task.portAppointment))
  if (!warehouse && !sawWarehouseStop) warehouse = parseAppt(task.warehouseAppointment)

  return { port, warehouse }
}

/**
 * The next appointment still ahead of the driver on this task.
 *
 * "When does this task need someone" — which is neither the first booking on it
 * nor the last. A pull booked Thursday that delivers Friday is Thursday's work
 * until the box is off the terminal, and Friday's the moment it is: the same
 * task, one day at a time, moving as it gets done. Filing it under the first
 * booking strands it in a day that has already been worked; filing it under the
 * last hides it from the day it actually has to be started.
 *
 * Route order decides, not clock order — the driver does the stops in sequence,
 * so a warehouse slot that happens to read earlier than the terminal slot is
 * still the second thing they do. Stop status is what marks a booking spent;
 * appointments have no "used" flag of their own.
 *
 * Same exclusions as the soonest: a stop marked no-appointment-required is not
 * a time, and a rejected stop is not a commitment.
 */
export function nextApptDate(task: ContainerTask, now: Date = new Date()): Date | null {
  let next: Date | null = null
  let last: Date | null = null

  for (const node of generateRouteNodes(task)) {
    if (node.appointmentRequired === false) continue
    if (node.action === 'rejected' || node.status === 'rejected') continue
    if (node.type !== 'port' && node.type !== 'warehouse') continue

    const at = parseAppt(node.appointmentTime)
    if (!at) continue
    last = later(last, at)
    // 'in-progress' is still ahead of them — they are standing in it.
    if (node.status === 'completed' || node.status === 'skipped') continue
    if (!next) next = at
  }

  if (next) return next
  // Every stop is behind them. The task is finished, or as good as: the last
  // booking is the truest thing left to say about when it happened.
  if (last) return last
  // Rows the generator can't build a route for still carry the flat columns,
  // and the board's own rule already reads those the way this one would.
  return effectiveApptDate(task, now)
}

/**
 * The one time a task sorts by.
 *
 * The terminal slot leads — that is what the day gets planned around. The
 * exception is a terminal slot that has already lapsed on an earlier day while
 * the warehouse still holds a live one: then the warehouse booking is the real
 * commitment, and burying the task back in the past would hide it.
 */
export function effectiveApptDate(task: ContainerTask, now: Date = new Date()): Date | null {
  const { port, warehouse } = getApptDates(task)
  if (!port || !warehouse) return port || warehouse

  const portDay = startOfDay(port)
  const warehouseDay = startOfDay(warehouse)
  if (portDay.getTime() === warehouseDay.getTime()) return port
  return portDay < startOfDay(now) ? warehouse : port
}

/**
 * Which day separator a task sits under.
 *
 * Anything already past folds into today's group rather than growing a tail of
 * expired-day headers above it — the board opens on today.
 */
export function apptDayKey(date: Date | null, now: Date = new Date()): string {
  if (!date) return NO_APPT_KEY
  // Compared as keys, not as Dates: a key is already the Pacific calendar day,
  // and ISO dates sort lexicographically, so this needs no second conversion —
  // and a round trip back through a Date would reintroduce the browser offset
  // this function exists to keep out.
  const today = todayKey(now)
  const day = dayKeyOf(date)
  return day < today ? today : day
}

/**
 * The day an appointment actually falls on — past days included.
 *
 * `apptDayKey` folds anything expired into today, which is right for a board
 * that opens on today and reads downward. It is wrong wherever a lapsed
 * appointment is the thing that needs looking at: folded in among today's work,
 * "this was due Friday and nobody ran it" reads as ordinary.
 */
export function exactDayKey(date: Date | null): string {
  return date ? dayKeyOf(date) : NO_APPT_KEY
}

/** The Pacific calendar day `now` falls on. */
export function todayKey(now: Date = new Date()): string {
  return dayKeyOf(now)
}

/** A dated day key that has already gone by. The undated sentinel never is. */
export function isPastDayKey(key: string, now: Date = new Date()): boolean {
  return key !== NO_APPT_KEY && key < todayKey(now)
}

/** 跑完了的活。派车确认窗口和司机卡都按这条分「计划」和「已经发生的事」。 */
const FINISHED_STATUSES = new Set(['completed', 'cancelled', 'uncompleted'])

/** 司机还扛在手上的活。跑完的是背景，没跑完的是计划。 */
export function isTaskFinished(task: ContainerTask): boolean {
  return FINISHED_STATUSES.has(task.status)
}

/**
 * 收了尾、日子不会再动的活。
 *
 * 和上面那条只差一个 `uncompleted`，但问的是两件事：`isTaskFinished` 问「这还算
 * 不算他手上剩下的活」（用来判要不要继续列出来），这一条问「这个日子还会不会跟
 * 着今天走」。`uncompleted` 是「换人了、上一段没跑完」—— 它还得有人接着干，所以
 * 它的日子还要动。
 *
 * 🔴 后端 `models/dispatchTask.model.js` 给司机 App 的那条 ORDER BY 里同一份名单，
 * 改一边必须改另一边。
 */
const SETTLED_STATUSES = new Set(['completed', 'cancelled'])

/** 这条活收尾了吗 —— 收了尾，它的日子和位次就地定住。 */
export function isTaskSettled(task: ContainerTask): boolean {
  return SETTLED_STATUSES.has(task.status)
}

/**
 * 这张任务挂在哪一天 —— **派车确认窗口和司机卡共用这一个答案**。
 *
 * 🔴 这个函数存在的唯一理由是：两块屏幕曾经各写各的，于是同一张任务在确认窗口
 * 里排在「Mon, Sep 21」，回到司机卡上却躺在「Today」。调度员拖好的一天，在他
 * 真正要用的那张卡上看不见（2026-09-21 用户实测）。
 *
 * 三条规矩，从上往下：
 *
 * 1. **调度员排过的那一天说了算**（`driverSortDay`，迁移 192）。
 *    这是「计划哪天跑」，而预约时间只是「码头给了哪个时段」—— 一趟 Live Load
 *    9/18 提的柜、9/21 还，调度员把它排在 9/21，那它就是 9/21 的活。以前这一列
 *    只被当成「手排位次还算不算数」的校验码，日子仍按预约时间算，结果就是
 *    「拖了等于没拖」。
 *
 * 2. **换人留下的那半段（`uncompleted`）按预约日算，过期折进今天**。
 *    它还得有人接着干，所以还是待办 —— 折进今天是为了别让它在卡顶上撑出一个
 *    过去的日期段，把真正要跑的活推下去。
 *
 *    🔴 但「没有预约」不等于「不知道哪天」：提空柜、场地内调度这类活压根不需要
 *    约票，跑完之后照预约日算就成了 NO_APPT ——「今天干完的活」从当天那一段里
 *    掉出去，卡上凭空少一条（2026-09-21 用户实测）。所以没票时退回调度员排的那
 *    一天，再退回今天：能进到卡上的完成任务，本来就只有今天完成的那些。
 *
 * 3. **剩下的按「下一个还没跑的站」那天算，且不折叠**。
 *    不折叠是 2026-09-21 用户拍板的：迟到的活单独挂在它迟到的那一天、标红，
 *    比混在今天里更容易被追。混进今天的过期活，看着和今天的活一模一样。
 *
 *    🔴 **跑完的活也走这一条**（2026-09-24 用户拍板：「完成的 task 就停留在完成
 *    之前的日期和顺序」）。原先它归第 2 条 —— 不看计划、按预约日、过期折进今天
 *    —— 而折叠那一下不看状态，于是已经跑完的活天天被搬到当天，又因为没被手拖过
 *    而垫在当天手排好的活后面：线上那张卡上，一条 9/23 约的还空柜在 9/24 显示成
 *    「今天的第四趟」，看着就是排序坏了。
 */
export function plannedDayKey(task: ContainerTask, now: Date = new Date()): string {
  if (isTaskFinished(task) && !isTaskSettled(task)) {
    const appt = effectiveApptDate(task, now)
    if (appt) return apptDayKey(appt, now)
    const today = todayKey(now)
    const planned = task.driverSortDay
    return planned && planned > today ? planned : today
  }
  const key = task.driverSortDay || exactDayKey(nextApptDate(task, now))
  // 一张票都没有的活算出来是 NO_APPT。还要跑的留在那一段没问题（它确实没日子）；
  // 已经收尾的不能，那就是上面第 2 条里记着的那个坑 —— 今天干完的活会从当天那
  // 一段掉出去，卡上凭空少一条。
  //
  // 🔴 但「记在今天」只对**今天**跑完的那些成立。一条从没约过票的完成任务，昨天
  // 跑完的和今天跑完的算出来一模一样，于是它天天赖在今天那一段里，日子过去也不会
  // 掉 —— 2026-09-29 线上截图：一位司机的今天挂着 11 条早就跑完的活。现在收尾那
  // 一刻由 DB 触发器把日子钉在任务上（`completed_day`，迁移 212），照那一天挂；
  // 只有钉不出日子的（迁移之前、又一条 timeline 事件都没有的）才退回今天。
  if (key === NO_APPT_KEY && isTaskSettled(task)) return task.completedDay || todayKey(now)
  return key
}

/**
 * 过去的那一天还值不值得列出来。
 *
 * 2026-09-29 用户拍板：**跑完的日子不占地方**。昨天以前的日子只有还压着没跑完的
 * 活才列 —— 整天都收了尾的，司机卡和派车确认窗口一律不再列。今天和往后照列，空
 * 的也照列（那是往上排活的地方）。
 *
 * 留下来的那一天里，跑完的那几条照常在：一天只要还欠着一条，旁边跑完的就是「这
 * 天干到哪儿了」的上下文，不是噪音。
 *
 * 这条取代了确认窗口原先那道「只留今天干完的」—— 那一道按 `updated_at` 判完成
 * 时间（`tasks` 没有完成时刻这一列），于是周一跑完、今天被同步碰过的活又算成
 * 「今天干完的」，赖在窗口里不走。按天判就不需要知道是哪一刻完成的。
 */
export function isSpentDay(
  key: string,
  tasks: (ContainerTask | null | undefined)[],
  now: Date = new Date()
): boolean {
  if (!isPastDayKey(key, now)) return false
  // 空的过去没有任何活撑着，同样不列。没有 task 的条目（确认窗口里的草稿）按
  // 「还没跑」算 —— 往过去的日子上排活，那一天当然要留着。
  return tasks.every((task) => !!task && isTaskFinished(task))
}

/** 按上面那条规矩，把整天都收了尾的过去日子从名单里摘掉。 */
export function dropSpentDays(tasks: ContainerTask[], now: Date = new Date()): ContainerTask[] {
  const byDay = new Map<string, ContainerTask[]>()
  for (const task of tasks) {
    const key = plannedDayKey(task, now)
    if (!byDay.has(key)) byDay.set(key, [])
    byDay.get(key)!.push(task)
  }
  const spent = new Set(
    Array.from(byDay, ([key, list]) => (isSpentDay(key, list, now) ? key : null)).filter(
      (key): key is string => key !== null
    )
  )
  if (spent.size === 0) return tasks
  return tasks.filter((task) => !spent.has(plannedDayKey(task, now)))
}

/**
 * 这张任务在某一天里的手排位次，没排过就是 null（迁移 192）。
 *
 * 位次只在「任务此刻仍属于当初手排的那一天」时算数。跑完的活现在也走第 1 条
 * （2026-09-24 拍板「日期和顺序都停留」），所以它的号也跟着留下；只有
 * `uncompleted` 那半段可能对不上 —— 它被折到今天，那就按时间排，不硬套一个
 * 过期的号。
 */
export function plannedOrder(task: ContainerTask, dayKey: string): number | null {
  const day = task.driverSortDay
  const order = task.driverSortOrder
  if (!day || typeof order !== 'number') return null
  return day === dayKey ? order : null
}

/**
 * 同一天之内谁在前。两块屏幕共用。
 *
 * 手排过的整体在前，按位次；没排过的跟在后面按时间 —— 新派进来的因此一律垫底，
 * 不会插进调度员排好的队里。
 */
export function comparePlannedTasks(
  a: ContainerTask,
  b: ContainerTask,
  dayKey: string,
  now: Date = new Date()
): number {
  const ao = plannedOrder(a, dayKey)
  const bo = plannedOrder(b, dayKey)
  if (ao !== null && bo !== null) return ao - bo
  if (ao !== null) return -1
  if (bo !== null) return 1
  const at = nextApptDate(a, now)?.getTime() ?? Number.MAX_SAFE_INTEGER
  const bt = nextApptDate(b, now)?.getTime() ?? Number.MAX_SAFE_INTEGER
  if (at !== bt) return at - bt
  // 一还一提：放柜那张在前（2026-09-22 线上报上来的）。
  //
  // 同一趟进闸办两件事，两张票的时间是同一个。时间打平之后原来直接按 id 定序，
  // 谁在上纯属编号巧合 —— 排反了不只是难看：卡片照这个顺序量空驶，于是「进码头
  // 放空柜、顺手提走重柜」被画成一段几十英里、方向还是反的空驶，钱也跟着算歪。
  //
  // 只认 taskType，不看状态、也不看路线：Live Load 在码头那一程会随着柜子出闸从
  // 「提」翻成「放」（utils/taskPairing 的 terminalSide），按它排会让一条任务跑到
  // 一半自己换位置 —— 和下面按 id 定序要躲的是同一个坑。
  const as = terminalSideRank(a)
  const bs = terminalSideRank(b)
  if (as !== bs) return as - bs
  // 时间一样时得有个跟状态、跟数据到达先后都无关的定序，否则「跑完一条，它就掉
  // 到最底下」：看板把没跑完和跑完了分两次请求拉回来再拼接，一条任务完成的那一
  // 刻就从前半段挪到了后半段，约在同一个点的两条因此整个换位。按 id 排，和后端
  // 发给司机 App 的那条 ORDER BY 末尾是同一条规矩，两块屏幕也就不会再各排各的。
  return compareTaskIds(a.id, b.id)
}

/**
 * 这一程在码头是放还是取 —— 放的排 0、取的排 1，同一时刻放在前。
 *
 * 后端给司机 App 的那条 ORDER BY 末尾是同一份名单，改一边必须改另一边。
 */
const DROP_FIRST: ReadonlySet<string> = new Set(['empty-in', 'load-in', 'return'])
function terminalSideRank(task: ContainerTask): number {
  return DROP_FIRST.has(task.taskType) ? 0 : 1
}

/**
 * 同一时间的两条任务谁在前 —— 只看 id。
 *
 * 真任务的 id 是数字，按数字比；FF 卡是 `ff-<n>` 那种，落回字符串比。两者混在
 * 一起时数字的在前 —— 谁前谁后不重要，同一份数据每次都给同一个答案才重要。
 */
function compareTaskIds(a: string | number, b: string | number): number {
  const an = Number(a)
  const bn = Number(b)
  const aNum = Number.isFinite(an)
  const bNum = Number.isFinite(bn)
  if (aNum && bNum) return an - bn
  if (aNum) return -1
  if (bNum) return 1
  return String(a).localeCompare(String(b))
}

/**
 * Today and the next `days - 1` Pacific days, in order.
 *
 * The planning horizon a dispatcher can drop work onto. Built from the day key
 * rather than by adding 24h to an instant, so a DST changeover doesn't quietly
 * skip or repeat a day.
 */
export function dayKeyHorizon(days: number, now: Date = new Date()): string[] {
  const [year, month, day] = todayKey(now).split('-').map(Number)
  const out: string[] = []
  for (let i = 0; i < days; i++) {
    const d = new Date(year, month - 1, day + i)
    out.push(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`)
  }
  return out
}

/** "Today" / "Monday, August 31" / "No Appointment". */
export function apptDayLabel(key: string, now: Date = new Date()): string {
  if (key === NO_APPT_KEY) return 'No Appointment'
  if (key === todayKey(now)) return 'Today'
  const [year, month, day] = key.split('-').map(Number)
  if (!year || !month || !day) return key
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * "Today" / "Mon, Aug 31" / "No Appt" — same day, fewer characters.
 *
 * For the narrow columns: driver cards in the sidebar, and the per-driver
 * sections of the confirmation modal. It must stay derived from the same key
 * as `apptDayLabel`, or a task could sit under "Today" in one panel and under
 * a dated header in another.
 */
export function apptDayLabelShort(key: string, now: Date = new Date()): string {
  if (key === NO_APPT_KEY) return 'No Appt'
  const [year, month, day] = key.split('-').map(Number)
  if (!year || !month || !day) return key
  const date = new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  // "Today" alone tells a dispatcher which day it is only if they already know
  // today's date, and half the reason to look at this panel is that they are
  // reading dates off a terminal screen that does not say "today".
  if (key === todayKey(now)) return `Today · ${date}`
  return `${new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'short' })}, ${date}`
}

/**
 * What a planned day contradicts, if anything.
 *
 * Nothing here blocks. A planned day is the dispatcher saying "this is the day
 * I mean to run it", and they know things the record does not — the terminal
 * extended a window, the warehouse agreed to take it late, the box is coming
 * off a vessel that slipped. So every one of these is a flag, never a refusal:
 * a guess that overrules the person holding the phone is worse than no guess.
 *
 * They are also deliberately few. A warning that fires on ordinary work stops
 * being read, and then the one that mattered goes past unseen too.
 */
export interface PlannedDayFlag {
  kind: 'terminal-appt' | 'after-delivery' | 'after-lfd'
  /** One line, already phrased for the card. */
  message: string
}

/** 'Sep 13' — short enough to sit inside a one-line flag. */
function shortDay(key: string): string {
  const [year, month, day] = key.split('-').map(Number)
  if (!year || !month || !day) return key
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Check a dispatcher's chosen day against what the record already commits to.
 *
 * `plannedKey` is only ever the day they dragged the card onto; a task sitting
 * on its own derived day has nothing to contradict and returns nothing.
 */
export function plannedDayFlags(
  task: ContainerTask,
  plannedKey: string | undefined,
  now: Date = new Date()
): PlannedDayFlag[] {
  if (!plannedKey || plannedKey === NO_APPT_KEY) return []
  const flags: PlannedDayFlag[] = []
  const { port, warehouse } = getApptDates(task)

  // A booked terminal slot is a real commitment on a real day. Planning around
  // a different one is legitimate — the slot may be about to be rescheduled —
  // but it must not be something the dispatcher can do without noticing.
  if (port) {
    const portKey = dayKeyOf(port)
    if (portKey !== plannedKey) {
      flags.push({
        kind: 'terminal-appt',
        message: `Terminal appointment is ${shortDay(portKey)}`,
      })
    }
  }

  // Pulling early and yard-storing is normal; pulling *after* the delivery slot
  // is not a plan, it is an impossibility.
  if (warehouse) {
    const warehouseKey = dayKeyOf(warehouse)
    if (plannedKey > warehouseKey) {
      flags.push({
        kind: 'after-delivery',
        message: `Delivery appointment is ${shortDay(warehouseKey)} — planned after it`,
      })
    }
  }

  // Past the last free day the box starts charging demurrage, every day.
  const lfd = parseAppt(task.lfd)
  if (lfd) {
    const lfdKey = dayKeyOf(lfd)
    if (plannedKey > lfdKey) {
      flags.push({
        kind: 'after-lfd',
        message: `LFD is ${shortDay(lfdKey)} — planned past it`,
      })
    }
  }

  return flags
}

/**
 * A soft colour per day, so two dates never read as one block of text.
 *
 * Keyed on the weekday rather than on the task's position in the list: a colour
 * picked by position would make the same Tuesday green in one driver's section
 * and amber in the next, which is worse than no colour at all. By weekday, a
 * date looks the same everywhere it appears — modal, driver card, any panel
 * that adopts this later — and two dates staged together are nearly always
 * different weekdays.
 *
 * Tints only, and deliberately no red, orange or amber in the set: those three
 * already mean "this task is in trouble" on the cards underneath. A day heading
 * that borrows them turns an ordinary Wednesday into a warning.
 */
const DAY_TONES = [
  { pill: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300', rule: 'border-fuchsia-300', band: 'bg-fuchsia-100', accent: 'border-fuchsia-400' }, // Sun
  { pill: 'bg-sky-100 text-sky-800 border-sky-300', rule: 'border-sky-300', band: 'bg-sky-100', accent: 'border-sky-400' },                         // Mon
  { pill: 'bg-emerald-100 text-emerald-800 border-emerald-300', rule: 'border-emerald-300', band: 'bg-emerald-100', accent: 'border-emerald-400' }, // Tue
  { pill: 'bg-violet-100 text-violet-800 border-violet-300', rule: 'border-violet-300', band: 'bg-violet-100', accent: 'border-violet-400' },       // Wed
  { pill: 'bg-lime-100 text-lime-800 border-lime-300', rule: 'border-lime-300', band: 'bg-lime-100', accent: 'border-lime-400' },                   // Thu
  { pill: 'bg-cyan-100 text-cyan-800 border-cyan-300', rule: 'border-cyan-300', band: 'bg-cyan-100', accent: 'border-cyan-400' },                   // Fri
  { pill: 'bg-indigo-100 text-indigo-800 border-indigo-300', rule: 'border-indigo-300', band: 'bg-indigo-100', accent: 'border-indigo-400' },       // Sat
]

const NO_APPT_TONE = {
  pill: 'bg-slate-100 text-slate-600 border-slate-300',
  rule: 'border-slate-300',
  band: 'bg-slate-100',
  accent: 'border-slate-400',
}

/**
 * The one day heading allowed to wear a warning colour.
 *
 * The weekday palette deliberately holds no red, orange or amber, because those
 * mean "this task is in trouble" on the cards underneath and an ordinary
 * Wednesday must not borrow them. A day that has already gone by while work
 * still sits on it is not an ordinary Wednesday — it is the trouble, at the
 * heading level, and the rule does not apply to the exception it exists for.
 */
const OVERDUE_TONE = {
  pill: 'bg-rose-100 text-rose-800 border-rose-300',
  rule: 'border-rose-300',
  band: 'bg-rose-100',
  accent: 'border-rose-400',
}

/**
 * Tailwind classes for a day heading.
 *
 * `pill` for the label, `rule` for a thin divider, `band` for a full-width
 * heading strip, `accent` for the rail that runs down the side of the day's
 * block. The last two are what make a day read as a block rather than as a
 * caption floating above some cards.
 *
 * `settled` 说的是这一天已经没什么欠着的 —— 底下每一条都收了尾。过去的那一天只
 * 在还有活压着的时候才是「出事了」；跑完的活现在留在它跑的那一天（见
 * `plannedDayKey` 第 3 条），把那天染成警告红等于把完成的活报成问题。
 */
export function dayTone(
  key: string,
  now: Date = new Date(),
  options: { settled?: boolean } = {}
): { pill: string; rule: string; band: string; accent: string } {
  if (key === NO_APPT_KEY) return NO_APPT_TONE
  const [year, month, day] = key.split('-').map(Number)
  if (!year || !month || !day) return NO_APPT_TONE
  // A day that has gone by outranks its weekday. Panels that fold expired work
  // into today never produce such a key, so nothing there changes.
  if (!options.settled && isPastDayKey(key, now)) return OVERDUE_TONE
  return DAY_TONES[new Date(year, month - 1, day).getDay()]
}

/**
 * Day keys in calendar order, with the undated group last.
 *
 * "No appointment" has no place on a timeline, and sorting its sentinel key as
 * a string drops it in the middle of the dated ones.
 */
export function compareDayKeys(a: string, b: string): number {
  if (a === b) return 0
  if (a === NO_APPT_KEY) return 1
  if (b === NO_APPT_KEY) return -1
  return a < b ? -1 : 1
}

/**
 * The day the container left the terminal, for a task that has no appointment.
 *
 * A return leg — empty-in, or its export twin load-in — is never booked against
 * a terminal slot, so it has no appointment and nothing above can order it. The
 * server carries the pickup leg's gate-out date onto it for exactly this
 * purpose; see PICKUP_GATE_OUT_JOIN in dispatchTask.model.js.
 */
export function pickupGateOutDate(task: ContainerTask): Date | null {
  return parseAppt(task.pickupGateOutTime)
}

/**
 * 柜子管理列表里会显示出闸日的那两个状态。
 *
 * 柜子已经离开码头、还没还回去，中间这一段就是 per-diem 在跑的那一段：Loading 是
 * 停在仓库卸货，Empty 是卸完了等还柜。其它状态要么柜子还在码头（没开始算），要么
 * 已经在还的路上/还完了（这个数不再是要看的数）。
 *
 * 行上那条标签和「这两个筛选默认按出闸日排序」共用这一个定义 —— 各写一份的下场
 * 就是某天两边不一致：列表按一个日期排序，行上却不显示那个日期。
 */
export const GATE_OUT_STATUSES = new Set(['loading', 'empty'])

/**
 * 出闸最早的排最前。
 *
 * 出来越久，per-diem 吃得越多，就越该先动它 —— 这一列的排序就是「先干哪只」。
 * 查不到出闸日的沉到底，彼此之间保持原来的顺序（Array#sort 是稳定的）。
 */
export function compareByGateOut(a: ContainerTask, b: ContainerTask): number {
  const da = pickupGateOutDate(a)
  const db = pickupGateOutDate(b)
  if (!da && !db) return 0
  if (!da) return 1
  if (!db) return -1
  return da.getTime() - db.getTime()
}

/**
 * Soonest appointment first.
 *
 * Tasks with no appointment sink below every booked one, and among themselves
 * fall back to when the box left the terminal, oldest first: the longer it has
 * been out, the longer per-diem has been running, so it is the one to move.
 * Only tasks with neither date are left unordered, at the very bottom.
 */
export function compareByAppt(a: ContainerTask, b: ContainerTask, now: Date = new Date()): number {
  const da = effectiveApptDate(a, now)
  const db = effectiveApptDate(b, now)
  if (!da && !db) {
    const ga = pickupGateOutDate(a)
    const gb = pickupGateOutDate(b)
    if (!ga && !gb) return 0
    if (!ga) return 1
    if (!gb) return -1
    return ga.getTime() - gb.getTime()
  }
  if (!da) return 1
  if (!db) return -1
  return da.getTime() - db.getTime()
}

/**
 * 路线第一站的预约时间。
 *
 * Unassigned 这一批还没派人，司机要卡的第一个时间点就是路线第一站那个约 ——
 * 不是全程最早的那个约。两者在有些单子上不是同一个：终点仓库的约可能比码头
 * 的约还早，`effectiveApptDate` 会挑到它，而这里要的就是第一站本身。
 *
 * 第一站写明「不需要预约」，或者压根没约，都算没有这个时间。
 */
export function firstStopApptDate(task: ContainerTask): Date | null {
  const [first] = generateRouteNodes(task)
  if (!first) return null
  if (first.appointmentRequired === false) return null
  return parseAppt(first.appointmentTime)
}

/**
 * 第一站预约时间从早到晚。
 *
 * 过期的不下沉 —— 日期已经过了的那些留在原来的位置上，靠颜色说话：行上那个
 * 时间会变成红的（见 `isPastApptDay`）。把它们踢到列表最底下等于藏起来，而
 * 「昨天那个约没赶上」恰恰是最需要有人立刻处理的一条。
 *
 * 第一站没有约的沉到最底，彼此保持原顺序（Array#sort 稳定）。
 */
export function compareByFirstStopAppt(a: ContainerTask, b: ContainerTask): number {
  const da = firstStopApptDate(a)
  const db = firstStopApptDate(b)
  if (!da && !db) return 0
  if (!da) return 1
  if (!db) return -1
  return da.getTime() - db.getTime()
}

/**
 * 这个预约的日期是不是已经过了。
 *
 * 只比日期，不比分秒：今天 13 号，那 12 号的才算过期；13 号早上八点的约到了
 * 下午仍然是今天的活，不该被标成过期。日期按太平洋时间算，和整套排期口径一致 ——
 * 换成浏览器本地时区，同一条记录在别的时区的人眼里会差一天。
 */
export function isPastApptDay(value?: string | Date | null, now: Date = new Date()): boolean {
  const d = value instanceof Date ? value : parseAppt(value)
  if (!d) return false
  return dayKeyOf(d) < dayKeyOf(now)
}

/**
 * 这只柜现在还握着一张码头的票吗 —— 也就是「还能不能去约」的那个判据。
 *
 * 和后端 LIVE_PORT_APPT_SQL 是同一条规矩，必须一起改：过了那天的票在码头那边
 * 已经作废（没去就是 no-show），不该再挡着重约；今天的票哪怕时间已经过了几小时
 * 仍然算活票，司机可能正拿着它在闸口。
 *
 * 两边判得不一样的代价，2026-09-15 线上见过一次：板子照常亮着预约按钮，
 * 后端却认定「这柜已经有票」，点多少次都只回一句话。
 */
export function hasLivePortAppt(task: ContainerTask, now: Date = new Date()): boolean {
  // 服务端已经判过死刑的那种：封闭区解除时票只剩不到两小时。按日期算它还是
  // 「今天的票」，可实际上跑不成了 —— 后端 LIVE_PORT_APPT_SQL 里同一条
  // （a.needs_rebook = false），必须一起改，不然板子和后端又会各说各话。
  if (task.portApptNeedsRebook) return false
  const { port } = getApptDates(task)
  if (!port) return false
  return !isPastApptDay(port, now)
}

/** 会默认按「第一站预约时间」排序的筛选。 */
export const FIRST_STOP_APPT_STATUSES = new Set(['unassigned'])

/**
 * The terminal a task belongs to — the key both the Terminal filter and the
 * Terminal grouping compare on, so a task can never be filtered into a group
 * it doesn't render under.
 */
export function terminalKey(task: ContainerTask): string {
  return task.portShortName || task.portName || ''
}

/** Label a task with no terminal groups under. */
export const NO_TERMINAL_LABEL = 'No Terminal'

/** The pickup gate-out marker a row shows in place of an appointment. */
export interface GateOutMarker {
  /** "Out 9/8 · 6d", or just "Out 9/8" for a box that hasn't left yet. */
  label: string
  /**
   * Whole days the box has been off the terminal, or null when the date is
   * still ahead of now — nothing has been counting yet, and "0d" would read as
   * if it had.
   */
  days: number | null
  /**
   * How hard the row should push. Free time is typically four or five days, so
   * the bands are set around it: still inside it, eating into it, past it.
   */
  tone: 'fresh' | 'aging' | 'stale'
  /** Spells out which of the three sources the date came from. */
  title: string
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The gate-out marker a row shows in place of an appointment.
 *
 * On the dispatch board this is only for tasks with no appointment at all: a
 * task that is booked already says what it is waiting on, and a second date next
 * to it would just compete for the reading. The tooltip names the source because
 * "appointment" and "completed" are stand-ins — showing them as a plain date
 * would pass a guess off as the moment the box actually cleared the gate.
 *
 * The date is what the board sorts by, but a bare date makes a dispatcher do the
 * subtraction on every row, so the days-out count rides along and drives the
 * colour. That count is what "this one first" actually means.
 *
 * `evenWhenBooked` drops the no-appointment condition, for the Container
 * Management list: nothing in its status cell shows an appointment, so there is
 * nothing to compete with, and per-diem keeps running on a booked box just the
 * same — hiding the count there would hide the one number that says how much the
 * box is costing.
 */
export function gateOutMarker(
  task: ContainerTask,
  now: Date = new Date(),
  options: { evenWhenBooked?: boolean } = {},
): GateOutMarker | null {
  if (!options.evenWhenBooked && effectiveApptDate(task, now)) return null
  const date = pickupGateOutDate(task)
  if (!date) return null

  // Counted in whole days between midnights, not in 24h blocks: a box that left
  // late yesterday evening has been out "1 day", which is how a yard talks about
  // it and how per-diem is charged. Elapsed hours would call it 0.
  const elapsed = Math.floor((startOfDay(now).getTime() - startOfDay(date).getTime()) / DAY_MS)
  const days = elapsed >= 0 ? elapsed : null
  const tone = days === null || days < 3 ? 'fresh' : days < 7 ? 'aging' : 'stale'

  const short = `${date.getMonth() + 1}/${date.getDate()}`
  const label = days === null ? `Out ${short}` : `Out ${short} · ${days}d`

  const full = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const title =
    task.pickupGateOutSource === 'actual'
      ? `Left the terminal ${full} (driver-reported gate-out)`
      : task.pickupGateOutSource === 'appointment'
        ? `Pickup was booked for ${full} — no gate-out reported`
        : `Pickup leg closed ${full} — no gate-out reported`

  return { label, days, tone, title }
}
/** 已完成那一行显示的完成日标签。 */
export interface CompletedMarker {
  /** "Done 9/8 · 2d" —— 当天干完的是 "· 0d"，和出闸日那条读法一致。 */
  label: string
  /** 说明这个日期是哪来的 —— 事件里的，还是任务被标记完成时写的。 */
  title: string
}

/**
 * 完成日标签。
 *
 * 只认服务端折算好的 'YYYY-MM-DD'：底层两个列都是不带时区的墙钟，当成瞬间解析
 * 会整体差一天（LFD 踩过）。所以这里按 '-' 拆开构造本地日期，绝不 `new Date(s)`。
 *
 * 后面跟的天数是「完成到今天隔了几天」，和出闸日那条一样按午夜之间的整天数算。
 * 未来日期（数据异常）按 0d 处理，不写负数。
 */
export function completedMarker(task: ContainerTask, now: Date = new Date()): CompletedMarker | null {
  const day = task.completedDay
  if (!day) return null
  const [year, month, date] = day.split('-').map(Number)
  if (!year || !month || !date) return null

  const completed = new Date(year, month - 1, date)
  const elapsed = Math.floor((startOfDay(now).getTime() - completed.getTime()) / DAY_MS)
  const short = `${month}/${date}`
  const label = `Done ${short} · ${Math.max(elapsed, 0)}d`

  const full = completed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const title =
    task.completedDaySource === 'event'
      ? `Marked completed ${full}`
      : `Completed ${full} (from the task's end time — no status event recorded)`

  return { label, title }
}

/**
 * 柜子管理列表里会显示建单日的那个状态。
 *
 * On Vessel 是「单已经建了、柜子还在船上」的那一段：码头那边什么都还没有，没有
 * 出闸日、没有完成日、LFD 多半也还是 Pending，整行唯一能说明「这单等了多久」
 * 的日期就是它被建出来的那天。其它状态要么码头已经给了日期，要么柜子已经在动，
 * 建单日不再是要看的数。
 *
 * 行上那条标签和「这个筛选默认按建单日排序」共用这一个定义 —— 各写一份的下场
 * 就是某天两边不一致：列表按一个日期排序，行上却不显示那个日期。
 */
export const ORDER_CREATED_STATUSES = new Set(['on-vessel'])

/** 建单日那条标签。 */
export interface OrderCreatedMarker {
  /** "Created 9/8"。 */
  label: string
  /** 悬停时给完整日期，省得人去猜 9/8 是哪一年。 */
  title: string
}

/**
 * 状态下面那一行建单日。
 *
 * 日期按太平洋日历日算，不按浏览器本地时区：`orders.created_at` 是不带时区的
 * 墙钟，prod 的后端跑在 UTC 上，直接 `toLocaleDateString` 等于拿浏览器的偏移去
 * 读一个 UTC 的时刻，深夜建的单会整整差一天。dayKeyOf 是整套排期统一的口径。
 */
export function orderCreatedMarker(task: ContainerTask): OrderCreatedMarker | null {
  const date = parseAppt(task.orderCreatedAt)
  if (!date) return null

  const [year, month, day] = dayKeyOf(date).split('-').map(Number)
  const label = `Created ${month}/${day}`
  const title = `Order created ${new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`

  return { label, title }
}

/**
 * 建单最早的排最前。
 *
 * 和出闸日那条同方向：等得最久的那单排最上面。柜子还在船上，调度员这一列要回答
 * 的是「哪一单压得最久、该先去盯」，不是「哪一单最新进来」—— 后者一直是接口的
 * 默认顺序，正是这里要盖掉的。
 *
 * 查不到建单日的沉到底，彼此之间保持原来的顺序（Array#sort 是稳定的）。
 */
export function compareByOrderCreated(a: ContainerTask, b: ContainerTask): number {
  const da = parseAppt(a.orderCreatedAt)
  const db = parseAppt(b.orderCreatedAt)
  if (!da && !db) return 0
  if (!da) return 1
  if (!db) return -1
  return da.getTime() - db.getTime()
}

/**
 * 完成最晚的排最前。
 *
 * 和出闸日那条方向相反，因为两列问的不是一回事：还在外面的柜子越久越烧钱，
 * 已经干完的活没什么可催的，最近完成的才是还要对账、还要回头看的那些。
 *
 * 直接比 'YYYY-MM-DD' 字符串：ISO 日期的字典序就是时间序，省掉一次解析，也就
 * 没有解析错时区的机会。查不到完成日的排在最后，彼此保持原有顺序。
 */
export function compareByCompletedDay(a: ContainerTask, b: ContainerTask): number {
  const da = a.completedDay
  const db = b.completedDay
  if (!da && !db) return 0
  if (!da) return 1
  if (!db) return -1
  return db.localeCompare(da)
}


/**
 * The board's row order.
 *
 * Plain appointment timeline by default. With terminal grouping on, the
 * terminal is the outer block and the timeline runs inside each one — tasks
 * with no terminal sink below the named blocks, the same way tasks with no
 * appointment sink below the booked ones.
 */
export function compareForBoard(
  a: ContainerTask,
  b: ContainerTask,
  options: { groupByTerminal?: boolean; now?: Date } = {},
): number {
  const now = options.now ?? new Date()
  if (options.groupByTerminal) {
    const ta = terminalKey(a)
    const tb = terminalKey(b)
    if (ta !== tb) {
      if (!ta) return 1
      if (!tb) return -1
      return ta.localeCompare(tb)
    }
  }
  return compareByAppt(a, b, now)
}

/**
 * 任务池里这张任务归在哪一天 —— 和日期分隔条用的是同一个答案。
 *
 * 单独抽出来是因为 Order 模式要靠它切上下两块：`NO_APPT_KEY` 的进"待配"，
 * 其余的进上面的时间轴。分隔条自己算一遍、分块再算一遍的话，迟早会出现
 * "分在下面那块、却顶着一个日期标题"的行。
 */
export function boardDayKey(task: ContainerTask, now: Date = new Date()): string {
  return apptDayKey(effectiveApptDate(task, now), now)
}

/** One row's separator state, or null when the board isn't in timeline order. */
export interface DayMarker {
  showDay: boolean
  label: string
  tone: 'today' | 'future' | 'none'
}

/**
 * Where the day separators fall across one page of rows.
 *
 * Computed per page, not across the whole pool: a page that opens in the middle
 * of a day still has to say which day it opened on, or the rows above the fold
 * belong to no announced day at all.
 *
 * A new terminal restarts the timeline, so its first day is re-announced even
 * when it repeats the day the previous block ended on.
 */
export function buildDayMarkers(
  tasks: ContainerTask[],
  options: { groupByTerminal?: boolean; now?: Date } = {},
): DayMarker[] {
  const now = options.now ?? new Date()
  const todayKey = apptDayKey(now, now)
  let prevDay: string | null = null
  let prevTerminal: string | null = null

  return tasks.map((task, index) => {
    const terminal = terminalKey(task) || NO_TERMINAL_LABEL
    const day = apptDayKey(effectiveApptDate(task, now), now)
    const newTerminal = !!options.groupByTerminal && (index === 0 || terminal !== prevTerminal)
    const showDay = index === 0 || day !== prevDay || newTerminal
    prevDay = day
    prevTerminal = terminal
    return {
      showDay,
      label: apptDayLabel(day, now),
      tone: day === NO_APPT_KEY ? 'none' : day === todayKey ? 'today' : 'future',
    }
  })
}
